import { createContext, useContext, ReactNode } from 'react';
import { TemplateType, TemplateConfig } from '../../types';

// Template configurations
export const TEMPLATES: TemplateConfig[] = [
  {
    id: 'atlassian',
    name: 'Atlassian Ecosystem',
    description: 'Jira, JSM, Confluence - Complete workflow design and export',
    icon: 'AT',
    features: [
      'JSM Assessment Card',
      'JSM Project Design Assistant',
      'Workflow & Request Type Designer',
      'Jira Export (CSV/JSON)',
      'Jira Backlog Generator (for CF)',
    ],
  },
  {
    id: 'custom',
    name: 'Custom ITSM',
    description: 'Platform-agnostic workflow design with universal export',
    icon: 'GN',
    features: [
      'Workflow Designer',
      'Request Type Builder',
      'CSV/Excel Export',
      'Documentation Generator',
    ],
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    description: 'ServiceNow ITSM workflow design and export',
    icon: 'SN',
    features: [
      'ServiceNow Workflow Designer',
      'Catalog Item Builder',
      'Update Set Export',
    ],
  },
  {
    id: 'ivanti',
    name: 'Ivanti',
    description: 'Ivanti ITSM workflow design and export',
    icon: 'IV',
    features: [
      'Ivanti Workflow Designer',
      'Request Type Builder',
      'Ivanti Export',
    ],
  },
  {
    id: 'bmc-helix',
    name: 'BMC Helix',
    description: 'BMC Helix ITSM workflow design and export',
    icon: 'BH',
    features: [
      'BMC Helix Workflow Designer',
      'Service Request Designer',
      'Smart IT Configuration',
      'CMDB Integration',
    ],
  },
  {
    id: 'freshservice',
    name: 'Freshservice',
    description: 'Freshservice ITSM workflow design with Freddy AI',
    icon: 'FS',
    features: [
      'Workflow Automator Designer',
      'Service Item Builder',
      'Freddy AI Configuration',
      'Modern UX Customization',
    ],
  },
];

interface TemplateContextValue {
  templateType: TemplateType;
  isAtlassian: boolean;
  isServiceNow: boolean;
  isIvanti: boolean;
  isBMCHelix: boolean;
  isFreshservice: boolean;
  isCustom: boolean;
  templateConfig: TemplateConfig;
}

const TemplateContext = createContext<TemplateContextValue | null>(null);

export function TemplateProvider({
  templateType,
  children,
}: {
  templateType: TemplateType;
  children: ReactNode;
}) {
  const templateConfig = TEMPLATES.find((t) => t.id === templateType);

  if (!templateConfig) {
    throw new Error(`Unknown template type: ${templateType}`);
  }

  const value: TemplateContextValue = {
    templateType,
    isAtlassian: templateType === 'atlassian',
    isServiceNow: templateType === 'servicenow',
    isIvanti: templateType === 'ivanti',
    isBMCHelix: templateType === 'bmc-helix',
    isFreshservice: templateType === 'freshservice',
    isCustom: templateType === 'custom',
    templateConfig,
  };

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>;
}

export function useTemplate() {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplate must be used within TemplateProvider');
  }
  return context;
}